// 📁 src/pages/OrderConfirmationPage.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Lottie from "lottie-react";
import successAnimation from "./assets/animations/success.json"; // ✅ Adjust path if needed

const OrderConfirmationPage = () => {
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    const storedOrder = localStorage.getItem("nutrafox_order");

    if (storedOrder) {
      const parsed = JSON.parse(storedOrder);

      // Generate orderId if missing
      if (!parsed.orderId) {
        parsed.orderId =
          "NF-" + Math.floor(Math.random() * 1000000).toString().padStart(6, "0");
      }

      // Add couponCode and discount if not already present
      if (!parsed.couponCode) {
        parsed.couponCode = localStorage.getItem("applied_coupon_code") || "";
      }

      if (!parsed.couponCurrency) {
        parsed.couponCurrency = localStorage.getItem("applied_coupon_currency") || "";
      }

      if (parsed.discount === undefined || parsed.discount === null) {
        const storedDiscount = localStorage.getItem("applied_coupon_discount");
        parsed.discount = storedDiscount ? parseFloat(storedDiscount) : 0;
      }

      localStorage.setItem("nutrafox_order", JSON.stringify(parsed));
      setOrder(parsed);
    } else {
      navigate("/"); // redirect if no order
    }
  }, [navigate]);

  if (!order) return null;

  const currencySymbols = {
    INR: "₹",
    USD: "$",
    EUR: "€",
    GBP: "£",
    AED: "د.إ",
  };

  const symbol = currencySymbols[order.currency] || "";

  return (
    <div className="min-h-screen bg-[#f3f0e4] py-16 px-4 text-[#105132]">
      <div className="max-w-3xl mx-auto bg-white p-8 rounded-xl shadow-md">
        <Lottie
          animationData={successAnimation}
          loop={true}
          className="w-32 mx-auto mb-4"
        />

        <h1 className="text-3xl font-bold mb-4 text-center">🎉 Thank you for your order!
        </h1>
        <p className="text-center text-gray-600 mb-8">
          Order ID: <strong>{order.orderId}</strong>
        </p>

        <h2 className="text-xl font-semibold mb-2">Customer Details</h2>
        <div className="mb-4 text-sm">
          <p><strong>Name:</strong> {order.fullName}</p>
          <p><strong>Email:</strong> {order.email}</p>
          <p><strong>Phone:</strong> {order.phone}</p>
          <p><strong>Address:</strong> {order.city}, {order.state}, {order.country} - {order.zip}</p>
          <p><strong>Delivery Method:</strong> {order.deliveryMethod}</p>
        </div>

        <h2 className="text-xl font-semibold mt-6 mb-2">Order Summary</h2>
        <div className="text-sm">
          {order.cart.map((item, idx) => {
            const rate = order?.rates?.[order.currency] || (order.subtotalConverted / order.subtotal) || 1;
            const itemTotal = item.price * item.quantity * rate;

            return (
              <div key={idx} className="flex justify-between mb-2">
                <span>{item.name} x {item.quantity}</span>
                <span>{symbol}{itemTotal.toFixed(2)} {order.currency}</span>
              </div>
            );
          })}

          <hr className="my-2" />

          <div className="flex justify-between">
            <span>Subtotal</span>
            <span>{symbol}{order.subtotalConverted?.toFixed(2)} {order.currency}</span>
          </div>

          {order.discountConverted > 0 && (
            <div className="flex justify-between text-[#c0a230]">
              <span>Discount</span>
              <span>- {symbol}{order.discountConverted?.toFixed(2)} {order.currency}</span>
            </div>
          )}

          {order.couponCode && (
            <div className="flex justify-between text-sm italic text-gray-500">
              <span>Coupon Used:</span>
              <span className="uppercase">{order.couponCode}</span>
            </div>
          )}

          <div className="flex justify-between">
            <span>Delivery Fee</span>
            <span>{symbol}{order.deliveryConverted?.toFixed(2)} {order.currency}</span>
          </div>

          <div className="flex justify-between font-bold border-t pt-2 mt-2 text-base">
            <span>Total Paid</span>
            <span>{symbol}{order.totalConverted?.toFixed(2)} {order.currency}</span>
          </div>
        </div>

        <div className="text-center mt-10">
          <button
            onClick={() => navigate("/products")}
            className="bg-[#105132] text-white px-6 py-3 rounded-full hover:bg-[#236e47] transition-all"
          >
            Continue Shopping →
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmationPage;
